import Api from '../apis/Api';
import GLU from '../glu2.js/src/index';
import AppConfig from '../appConfig';
import UserInfo from '../objects/UserInfo';
import UserPreferences from '../objects/UserPreferences';
import Mixpanel from '../helpers/Mixpanel';

class UserInfoDataSource extends GLU.DataSource {
    static get name() {
        return 'UserInfoDataSource';
    }

    static getInstance() {
        return new UserInfoDataSource();
    }

    get authParams() {
        return {
            geobufferToken: this.currentUser.geobufferToken,
            geobufferPermission: this.currentUser.geobufferPermission,
        };
    }

    /**
     * A getter for currently logged in user.
     * If the user is not logged in, the userID
     * value is -1 and user has only guest
     * privileges.
     * @returns {UserInfo}
     */
    get currentUser() {
        return this._currentUser;
    }

    /**
     * @returns {UserPreferences}
     */
    get userPreferences() {
        return this._currentUser ? this._currentUser.preferences : undefined;
    }

    normalizePreferences = (backendPrefs = {}) => {
        const preferences = new UserPreferences();

        preferences.analysisType =
            backendPrefs.analysisType ||
            backendPrefs.analysis_type ||
            AppConfig.constants.defaultLocationAnalysisSelectionType;

        preferences.analysisValues =
            backendPrefs.analysisValues || backendPrefs.analysis_values || {};

        preferences.walkthroughs = backendPrefs.walkthroughs || {};

        preferences.mapDefaults = backendPrefs.mapDefaults || backendPrefs.map_defaults || {};

        preferences.myDealsActiveList =
            backendPrefs.myDealsActiveList || backendPrefs.my_deals_active_list || {};

        return preferences;
    };

    /**
     * Function that retrieves user/session info
     * @returns {Promise}
     */
    loadUserInfo() {
        return new Promise((resolve, reject) => {
            Api.session.sessionInfo().then(response => {
                const sessionInfo = response.body;

                if (!response.ok) {
                    reject('Failed to load user info');
                    return;
                }

                const userInfo = new UserInfo();

                const user = sessionInfo.user;
                userInfo.activeSubscriptions = sessionInfo.active_subscriptions.map(
                    subscription => ({
                        ...subscription,
                        products: subscription.products.map(product => ({
                            productId: product.product_id,
                            configuration: product.configuration || {},
                        })),
                    }),
                );
                if (user) {
                    userInfo.userId = user.id;
                    userInfo.name = this.displayName(user.first_name, user.last_name);

                    userInfo.pictureSmallURL = user.picture_small_url;
                    userInfo.pictureLargeURL = user.picture_large_url;
                    userInfo.email = user.email;
                    userInfo.activeOrganizationId = user.active_organization_id;

                    if (user.preferences) {
                        userInfo.preferences = this.normalizePreferences(user.preferences);
                    }

                    Mixpanel.identifyAndSetUser(
                        userInfo.userId,
                        userInfo.name,
                        userInfo.getUserProduct(),
                        user.email,
                        userInfo.activeOrganizationId,
                    );
                } else {
                    userInfo.userId = -1;
                }

                userInfo.product = sessionInfo.legacy_license.product;
                userInfo.licenseInfo = sessionInfo.legacy_license.info;

                this._currentUser = userInfo;

                AppConfig.sentry.configureScope(scope => {
                    scope.setUser({
                        username: userInfo.name,
                        id: userInfo.userId,
                    });
                });

                resolve(this._currentUser);
            }, reject);
        });
    }

    logOut() {
        return new Promise((resolve, reject) => {
            Api.session.logOut().then(response => {
                const user = response.body;
                const userInfo = new UserInfo();
                userInfo.userId = user.id;
                this._currentUser = userInfo;
                resolve(userInfo);
            }, reject);
        });
    }

    updateUserPreferences(payload) {
        return new Promise((resolve, reject) => {
            Api.accounts
                .updateUserPreferences({ payload })
                .then(response => {
                    if (!response.ok) {
                        reject('Failed to update user preferences');
                        return;
                    }
                    const updatedUser = response.body;
                    const existingPrefs =
                        this._currentUser && this._currentUser.preferences
                            ? this._currentUser.preferences
                            : new UserPreferences();

                    if (updatedUser.preferences) {
                        const normalized = this.normalizePreferences(updatedUser.preferences);

                        normalized.walkthroughs = {
                            ...(existingPrefs.walkthroughs || {}),
                            ...(normalized.walkthroughs || {}),
                        };

                        this._currentUser.preferences = normalized;
                    }
                    resolve(this._currentUser);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    displayName(firstName, lastName) {
        return [firstName, lastName].filter(Boolean).join(' ');
    }
}

export default UserInfoDataSource;
